import { forwardRef, useId } from "react";
import { Icon } from "./icon";
import { cx } from "@/utils/cx";
import type { SelectHTMLAttributes } from "react";

/**
 * Gymkee 2027 select (spec select 1.1; sheet DS05).
 *
 * The same well, the same corner and the same focus ring as a text field, with the chevron from
 * the library at the trailing edge. The native `<select>` sits inside the frame with its own
 * chrome removed: on this surface the list is a country list, which is longer than the eight
 * options a drawn menu is for, and the platform's own picker is what a phone should open for it.
 */

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "size"
> {
  label: string;
  options: SelectOption[];
  /** Shown as the first, unselectable entry when nothing is chosen yet. */
  placeholder?: string;
  error?: string | null;
  className?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      placeholder,
      error,
      className,
      id: givenId,
      disabled,
      ...props
    },
    ref,
  ) => {
    const generated = useId();
    const id = givenId ?? `select-${generated}`;
    const messageId = `${id}-message`;

    return (
      <div className={cx("gap-gk-2 flex flex-col", className)}>
        <label htmlFor={id} className="ds-label">
          {label}
        </label>
        <div
          className={cx("ds-field", {
            "ds-field-error": !!error,
            "ds-field-disabled": !!disabled,
          })}
          aria-disabled={disabled || undefined}
        >
          <select
            ref={ref}
            id={id}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? messageId : undefined}
            className="ds-field-input w-full cursor-pointer appearance-none bg-transparent"
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="ds-field-affix">
            <Icon name="chevron-down" size={20} />
          </span>
        </div>
        {error && (
          <p id={messageId} className="ds-error-message">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";
